<script setup>
// Опитування в чаті ефіру: питання, варіанти, лічильник часу і результат.
//
// Картка висить угорі стрічки, а не окремим рядком у ній: у чаті, що біжить,
// повідомлення з питанням поїхало б угору за пів хвилини — тобто рівно
// тоді, коли по ньому починають клікати.
//
// Результат видно одразу, ще під час голосування, — як у Twitch: чат і так
// пише «я за друге», і ховати числа означало б ховати їх лише від тих, хто
// не встиг прочитати стрічку.

const props = defineProps({
  poll: { type: Object, required: true },
  // Голосувати може лише залогінений: голоси рахуються по глядачах, а не
  // по відкритих вкладках
  canVote: { type: Boolean, default: false },
  canModerate: { type: Boolean, default: false }
})

const emit = defineEmits(['vote', 'end', 'drop'])

// Скільки лишилось. Годинник цокає тут, а не в стрічці: без нього рядок
// «30 секунд» застигав би до наступного чужого голосу
const now = ref(Date.now())
let clock = null

onMounted(() => {
  clock = setInterval(() => {
    now.value = Date.now()
  }, 1000)
})

onBeforeUnmount(() => {
  if (clock) clearInterval(clock)
})

const left = computed(() => {
  if (props.poll.closed) return 0
  const end = new Date(props.poll.endsAt).getTime()
  if (Number.isNaN(end)) return 0
  return Math.max(0, Math.round((end - now.value) / 1000))
})

const countdown = computed(() => {
  const seconds = left.value
  if (seconds >= 60) return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
  return `${seconds} с`
})

const total = computed(() => props.poll.total || 0)

// «1 голос», «2 голоси», «5 голосів» — українська відміна після числа. Без
// цього завершене опитування підписувалось «1 голосів»
const votesLabel = computed(() => {
  const n = total.value
  const tail = n % 100
  const last = n % 10
  if (tail >= 11 && tail <= 14) return `${n} голосів`
  if (last === 1) return `${n} голос`
  if (last >= 2 && last <= 4) return `${n} голоси`
  return `${n} голосів`
})

const share = (votes) => (total.value ? Math.round((votes / total.value) * 100) : 0)

// Переможець підсвічуємо тільки в завершеному: під час голосування лідер
// міняється щосекунди, і смуга миготіла б кольором
const winner = computed(() => {
  if (!props.poll.closed || !total.value) return -1
  let best = 0
  props.poll.options.forEach((option, index) => {
    if (option.votes > props.poll.options[best].votes) best = index
  })
  return props.poll.options[best].votes ? best : -1
})

const vote = (index) => {
  if (!props.canVote || props.poll.closed) return
  emit('vote', index)
}
</script>

<template>
  <div class="flex-shrink-0 bg-base-200 px-2 py-1.5 border-primary/40 border-b-2">
    <div class="flex items-center gap-1.5 mb-1">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        class="text-primary shrink-0"
      >
        <line
          x1="6"
          y1="20"
          x2="6"
          y2="12"
        />
        <line
          x1="12"
          y1="20"
          x2="12"
          y2="4"
        />
        <line
          x1="18"
          y1="20"
          x2="18"
          y2="14"
        />
      </svg>

      <span class="font-semibold text-[11px] text-primary">
        {{ poll.closed ? 'Опитування завершено' : 'Опитування' }}
      </span>

      <span
        v-if="!poll.closed"
        class="ml-auto text-[11px] text-base-content/60 tabular-nums"
        :class="{ 'text-warning': left <= 10 }"
        >{{ countdown }}</span
      >
      <span
        v-else
        class="ml-auto text-[10px] text-base-content/45"
        >{{ votesLabel }}</span
      >

      <!-- Модератор може завершити раніше (усі вже проголосували) або
           прибрати картку зовсім, коли результат уже нікому не потрібен -->
      <button
        v-if="canModerate && !poll.closed"
        class="text-[10px] text-base-content/50 hover:text-warning"
        title="Завершити голосування зараз"
        @click="emit('end')"
      >
        завершити
      </button>
      <button
        v-else-if="canModerate"
        class="text-base-content/50 hover:text-error"
        title="Прибрати опитування з чату"
        @click="emit('drop')"
      >
        ✕
      </button>
    </div>

    <p class="mb-1.5 font-semibold text-[13px] leading-snug break-words">
      {{ poll.question }}
    </p>

    <div class="flex flex-col gap-1">
      <!--
        Кнопка і смуга результату — це один елемент, а не два: показувати
        числа окремо від того, за що тиснути, у колонці шириною в чат нема де
      -->
      <button
        v-for="(option, index) in poll.options"
        :key="index"
        type="button"
        class="relative rounded overflow-hidden text-left transition-colors"
        :class="[
          canVote && !poll.closed ? 'cursor-pointer hover:bg-base-100' : 'cursor-default',
          poll.myVote === index ? 'ring-1 ring-primary' : ''
        ]"
        :disabled="!canVote || poll.closed"
        @click="vote(index)"
      >
        <!-- Смуга під текстом: доля голосів. Ширина в процентах, а не
             градієнт, — так її видно і на найвужчій колонці -->
        <span
          class="absolute inset-y-0 left-0 transition-[width] duration-300"
          :class="
            winner === index
              ? 'bg-success/35'
              : poll.myVote === index
                ? 'bg-primary/35'
                : 'bg-base-content/10'
          "
          :style="{ width: `${share(option.votes)}%` }"
        />

        <span class="relative flex items-center gap-1.5 px-2 py-1 text-[12px]">
          <span class="truncate">{{ option.text }}</span>
          <span
            v-if="poll.myVote === index"
            class="text-[10px] text-primary shrink-0"
            >ваш</span
          >
          <span class="ml-auto text-[11px] text-base-content/60 tabular-nums shrink-0">
            {{ share(option.votes) }}% · {{ option.votes }}
          </span>
        </span>
      </button>
    </div>

    <p
      v-if="!canVote && !poll.closed"
      class="mt-1 text-[10px] text-base-content/45"
    >
      Увійдіть, щоб проголосувати
    </p>
  </div>
</template>
